import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import type { User } from '../api/user'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'
import AdminBaseModal from '../modals/AdminBaseModal'
import AdminUserDetailModal from '../models/AdminUserDetailModal'

const AdminUsers: React.FC = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const fetchUsers = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await adminApi.getUsers({
        page: currentPage,
        per_page: 20,
        search: search || undefined,
      })
      setUsers(response.data)
      setLastPage(response.last_page)
      setTotal(response.total)
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('사용자 목록을 불러오는데 실패했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, search, navigate])

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (!adminToken) {
      navigate('/admin/login')
      return
    }
    fetchUsers()
  }, [navigate, fetchUsers])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchUsers()
  }

  const handleLogout = () => {
    localStorage.removeItem('admin_token')
    navigate('/admin/login')
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getSocialProviderName = (provider: string | null): string => {
    if (!provider) return ''
    switch (provider) {
      case 'google':
        return '구글'
      case 'kakao':
        return '카카오'
      case 'naver':
        return '네이버'
      default:
        return provider
    }
  }

  const getSocialProviderColor = (provider: string | null): string => {
    if (!provider) return ''
    switch (provider) {
      case 'google':
        return 'bg-white border border-black text-black hover:bg-gray-50'
      case 'kakao':
        return 'bg-[#FEE500] hover:bg-[#FDD835] text-black'
      case 'naver':
        return 'bg-[#03C75A] hover:bg-[#02B350] text-white'
      default:
        return 'bg-4B3621-80% hover:bg-4B3621-80%-70% text-white'
    }
  }

  const handleViewDetail = (user: User) => {
    setSelectedUser(user)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedUser(null)
  }

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={handleLogout}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px">
          <div className="bg-white rounded-16px shadow-sm p-32px">
            <div className="flex items-center justify-between mb-24px">
              <h2 className="text-style-title2 text-textColorText2">
                사용자 관리
              </h2>
              <div className="text-style-body2 text-textColorText2">
                총 {total.toLocaleString()}명
              </div>
            </div>

            <form onSubmit={handleSearch} className="mb-24px">
              <div className="flex gap-12px">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="아이디, 이메일, 닉네임, 휴대폰 번호로 검색"
                  className={`
                  flex-1
                  py-12px
                  px-16px
                  border
                  border-brandPrimary-20%
                  rounded-8px
                  text-style-body1
                  text-textColorText2
                  bg-transparent
                  focus:outline-none
                  focus:border-brandPrimary
                  placeholder:text-textColorText2
                `}
                />
                <button
                  type="submit"
                  className="py-12px px-24px bg-4B3621-80% text-white rounded-8px text-style-button2 hover:bg-4B3621-80%-70% transition-colors"
                >
                  검색
                </button>
              </div>
            </form>

            {isLoading ? (
              <div className="flex justify-center items-center py-80px">
                <div className="text-style-body2 text-textColorText2">
                  로딩 중...
                </div>
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-80px">
                <div className="text-style-body2 text-textColorText2 mb-8px">
                  사용자가 없습니다.
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-brandPrimary-10%">
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          ID
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          아이디
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          소셜 / 이메일
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          닉네임
                        </th>
                        <th className="text-right py-12px px-16px text-style-labelM text-textColorText2">
                          기록
                        </th>
                        <th className="text-right py-12px px-16px text-style-labelM text-textColorText2">
                          저장
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          가입일
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          탈퇴 여부
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          관리
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr
                          key={user.id}
                          className="border-b border-brandPrimary-5% hover:bg-backgroundColorBg2 transition-colors"
                        >
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {user.id}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {user.username}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {/*
                              소셜을 먼저 보여준다.

                              예전에는 이메일이 있으면 거기서 끝나고 소셜은
                              '이메일이 없을 때의 대체 표시'였다. 소셜 가입자는
                              이메일이 없다는 전제였는데, 둘 다 가진 계정이
                              생기면서(공식 계정) 연동해도 화면에 안 나타났다.
                              이제 이메일은 새 가입에서 받지 않으므로 소셜이 주다.
                            */}
                            {user.is_social_login && user.social_provider ? (
                              <span className="inline-flex items-center gap-8px">
                                <span
                                  className={`
                                    inline-flex
                                    items-center
                                    py-4px
                                    px-12px
                                    rounded-6px
                                    text-12px
                                    font-medium
                                    ${getSocialProviderColor(
                                      user.social_provider
                                    )}
                                    transition-colors
                                  `}
                                >
                                  {getSocialProviderName(user.social_provider)}
                                </span>
                                {user.email && (
                                  <span className="text-12px text-textColorText3">
                                    {user.email}
                                  </span>
                                )}
                              </span>
                            ) : user.email ? (
                              user.email
                            ) : (
                              <span className="text-textColorText3">-</span>
                            )}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {user.nickname}
                          </td>
                          {/*
                            기록·저장은 숫자라 오른쪽 정렬하고 tabular-nums 를 준다.
                            자릿수가 흔들리지 않아 위아래로 훑기 쉽다.
                            0 은 흐리게 — 한눈에 "안 쓰는 계정"이 보인다.
                          */}
                          <td
                            className={`py-12px px-16px text-style-body2 text-right tabular-nums ${
                              user.review_count ? 'text-textColorText2' : 'text-textColorText2 opacity-35'
                            }`}
                          >
                            {user.review_count ?? 0}
                          </td>
                          <td
                            className={`py-12px px-16px text-style-body2 text-right tabular-nums ${
                              user.saved_place_count ? 'text-textColorText2' : 'text-textColorText2 opacity-35'
                            }`}
                          >
                            {user.saved_place_count ?? 0}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {formatDate(user.created_at)}
                          </td>
                          <td className="py-12px px-16px">
                            <span
                              className={`text-style-body2 ${
                                user.is_active
                                  ? 'text-textColorText2'
                                  : 'text-semanticError'
                              }`}
                            >
                              {user.is_active ? '활성' : '탈퇴'}
                            </span>
                          </td>
                          <td className="py-12px px-16px">
                            <button
                              onClick={() => handleViewDetail(user)}
                              className={`
                                py-8px
                                px-16px
                                bg-4B3621-80%
                                text-white
                                rounded-8px
                                text-style-button2
                                hover:bg-4B3621-80%-70%
                                transition-colors
                              `}
                            >
                              상세보기
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {lastPage > 1 && (
                  <div className="flex items-center justify-center gap-8px mt-32px">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      className={`
                      py-8px
                      px-16px
                      rounded-8px
                      text-style-button2
                      border
                      ${
                        currentPage === 1
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }
                    `}
                    >
                      이전
                    </button>
                    <div className="text-style-body2 text-textColorText2">
                      {currentPage} / {lastPage}
                    </div>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(lastPage, prev + 1))
                      }
                      disabled={currentPage === lastPage}
                      className={`
                      py-8px
                      px-16px
                      rounded-8px
                      text-style-button2
                      border
                      ${
                        currentPage === lastPage
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }
                    `}
                    >
                      다음
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <AdminBaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="사용자 상세 정보"
        maxWidth="max-w-[800px]"
        overlayOpacity="bg-opacity-50"
      >
        {selectedUser && (
          <AdminUserDetailModal
            user={selectedUser}
            onClose={handleCloseModal}
            formatDate={formatDate}
            onUpdate={fetchUsers}
            onDeleteSuccess={fetchUsers}
          />
        )}
      </AdminBaseModal>
    </div>
  )
}

export default AdminUsers
